import React, {useState, useEffect} from 'react';
import {Box, Button, ScrollView, Text, VStack, HStack, Checkbox, Stack} from 'native-base';
import ApproveJob from './ApproveJob';
import AppContainer from '../Components/AppContainer';
import Trans from '../Components/Trans';
import GeneralAction from '../../Actions/GeneralAction';
import ProjectDepartmentFilters from '../Components/ProjectDepartmentFilters';
import APIAction from '../../Actions/APIAction';

const ApproveJobs = ({route, navigation}) => {
  // Init
  let ids = [];
  const [trigger, setTrigger] = useState(0);
  const [isApproving, setIsApproving] = useState(false);

  // Multi-select for individual approval
  const [selectedJobIds, setSelectedJobIds] = useState([]);

  // Filter state
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [jobsData, setJobsData] = useState({});

  // Get id
  if (route.params) {
    if (route.params.ids) {
      ids = route.params.ids;
    }
  }

  // Fetch job details for filters (departments are fetched by ProjectDepartmentFilters)
  useEffect(() => {
    const fetchJobData = async () => {
      const jobsMap = {};
      const projectsSet = new Map();

      for (const id of ids) {
        const job = await APIAction.request({
          method: 'GET',
          url: `/api/employee_shifts/${id}`,
        });
        if (job) {
          jobsMap[id] = job;

          // Extract unique projects
          if (job.shift?.project) {
            projectsSet.set(job.shift.project.id, job.shift.project);
          }
        }
      }

      setJobsData(jobsMap);
      setProjects(Array.from(projectsSet.values()));
    };

    if (ids.length > 0) {
      fetchJobData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.length]);

  // Filter jobs based on selected filters (using names, not IDs)
  const getFilteredIds = () => {
    return ids.filter(id => {
      const job = jobsData[id];
      if (!job) return true; // Show while loading

      // Match project by name
      const matchesProject =
        selectedProjects.length === 0 ||
        selectedProjects.includes(job.shift?.project?.name);

      // Match department by name (case-insensitive), including "none" for no department
      const jobDepartment = job.shift?.department?.name || 'none';
      const matchesDepartment =
        selectedDepartments.length === 0 ||
        selectedDepartments.some(selected =>
          selected.toLowerCase() === jobDepartment.toLowerCase()
        );

      return matchesProject && matchesDepartment;
    });
  };

  const filteredIds = getFilteredIds();

  const onToggleJobHandler = jobId => {
    if (selectedJobIds.includes(jobId)) {
      setSelectedJobIds(selectedJobIds.filter(id => id !== jobId));
      return;
    }

    setSelectedJobIds([...selectedJobIds, jobId]);
  };

  const onSelectAllJobsHandler = () => {
    if (selectedJobIds.length === filteredIds.length) {
      setSelectedJobIds([]);
      return;
    }

    setSelectedJobIds([...filteredIds]);
  };

  const onApproveSelectedHandler = async () => {
    if (selectedJobIds.length === 0) {
      return;
    }

    setIsApproving(true);
    setTrigger(trigger + 1);

    // Wait a moment for all approvals to process
    // This is handled by the trigger mechanism in child components
    setTimeout(() => {
      setIsApproving(false);

      GeneralAction.toast(
        'success',
        <Trans>Jobs have been approved</Trans>
      );

      navigation.replace('Base');
    }, 2000);
  };

  return (
    <AppContainer>
      <ScrollView>
        <VStack space={3}>
          {/* Filters */}
          <Box mx={3} mt={2}>
            <ProjectDepartmentFilters
              projects={projects}
              onChange={({selectedProjects: projs, selectedDepartments: depts}) => {
                setSelectedProjects(projs);
                setSelectedDepartments(depts);
              }}
            />
          </Box>

          {/* Header with Select All and Approve buttons */}
          <Stack
            direction={{base: 'column', md: 'row'}}
            justifyContent={{base: 'flex-start', md: 'space-between'}}
            alignItems={{base: 'flex-start', md: 'center'}}
            space={2}
            px={3}>
            <Text fontSize={'lg'} fontWeight={'600'}>
              <Trans>Hours to approve</Trans>
              {filteredIds && filteredIds.length > 0 && (
                <Text fontSize={'md'} color={'gray.500'} fontWeight={'normal'}>
                  {' '}
                  ({filteredIds.length})
                </Text>
              )}
            </Text>
            <HStack space={2} flexWrap={'wrap'}>
              <Button
                size={'sm'}
                variant={'outline'}
                onPress={onSelectAllJobsHandler}>
                <Text fontSize={'xs'}>
                  {selectedJobIds.length === filteredIds.length && filteredIds.length > 0 ? (
                    <Trans>Deselect All</Trans>
                  ) : (
                    <Trans>Select All</Trans>
                  )}
                </Text>
              </Button>
              {selectedJobIds.length > 0 && (
                <Button
                  size={'sm'}
                  colorScheme={'primary'}
                  onPress={onApproveSelectedHandler}
                  isLoading={isApproving}
                  isDisabled={isApproving}>
                  <Text color={'white'} fontSize={'xs'} fontWeight={'600'}>
                    <Trans>Approve</Trans> ({selectedJobIds.length})
                  </Text>
                </Button>
              )}
            </HStack>
          </Stack>

          {/* Jobs list with checkboxes */}
          {filteredIds.map(id => {
            return (
              <Box
                key={id}
                style={{
                  backgroundColor: selectedJobIds.includes(id) ? '#e3f2fd' : '#fff',
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: selectedJobIds.includes(id) ? '#2196f3' : '#e9ecef',
                  marginHorizontal: 12,
                }}>
                <HStack space={3} alignItems={'flex-start'} p={2}>
                  <Checkbox
                    value={id}
                    isChecked={selectedJobIds.includes(id)}
                    onChange={() => onToggleJobHandler(id)}
                    colorScheme={'primary'}
                    mt={2}
                  />
                  <Box flex={1}>
                    <ApproveJob
                      route={route}
                      navigation={navigation}
                      id={id}
                      trigger={trigger}
                      selectedJobIds={selectedJobIds}
                      stopApproving={() => {
                        setIsApproving(false);
                      }}
                    />
                  </Box>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </ScrollView>
    </AppContainer>
  );
};

export default ApproveJobs;
